import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, input, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TranslatePipe } from '@next/core-lib/i18n';

@Component({
    selector: 'app-nutrient-label-column',
    template: `
    <div class="container">
      <!-- First Container: Plant Parameters -->
      @if (isPlant()) {
        <div class="plant-parameters">
          @for (param of getPlantParams(); track $index) {
            <div class="parameter-item">
              <span class="parameter-translation">{{ getDisplayParam($index) | translate }}</span>
              <span class="parameter-unit">{{ getUnit(plantParameters[$index]) }}</span>
            </div>
          }
        </div>
      } @else {
        <!-- Second Container: Soil Parameters -->
        <div class="soil-parameters">
          @if (hasSoilNitrate()) {
            <div class="parameter-item">
              <span class="parameter-translation">{{ getDisplayParam(0) | translate }}</span>
              <span class="parameter-unit">{{ getUnit(soilParameters[0]) }}</span>
            </div>
          }

          @for (param of getSoilParamsForSingleCropSoilAnalysis(); track $index) {
            <div class="parameter-item">
              <span class="parameter-translation">{{ getDisplayParam($index + 1) | translate }}</span>
              <span class="parameter-unit">{{ getUnit(soilParameters[$index + 1]) }}</span>
            </div>
          }
        </div>
      }
    </div>
  `,
    styleUrl: './nutrient-label-column.component.scss',
    imports: [TranslatePipe],
})
export class NutrientLabelColumnComponent implements OnInit, OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);

  hasSoilNitrate = input<boolean>();
  isPlant = input<boolean>();
  readonly destroy$ = new Subject<void>();

  // Desktop parameters
  plantParameters = ['Plant_N', 'Plant_K', 'Plant_Mn', 'Foliage_weight', 'Nitrate_standard'];
  soilParameters = ['Soil_min_N', 'Soil_K', 'Soil_Mg', 'Soil_Mn', 'Soil_B', 'Soil_S'];

  // Mobile parameters
  plantParametersMobile = ['Plant_N_mobile', 'Plant_K_mobile', 'Plant_Mn_mobile', 'Foliage_weight_mobile', 'Nitrate_standard_mobile'];
  soilParametersMobile = ['Soil_min_N_mobile', 'Soil_K_mobile', 'Soil_Mg_mobile', 'Soil_Mn_mobile', 'Soil_B_mobile', 'Soil_S_mobile'];

  isMobile = false;

  ngOnInit() {
    this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      '(max-width: 1235px)',
    ]).pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });
  }

  getPlantParams(): string[] {
    return this.plantParameters;
  }

  getSoilParamsForSingleCropSoilAnalysis(): string[] {
    return this.soilParameters.slice(1);
  }

  getDisplayParam(index: number): string {
    if (this.isPlant()) {
      if (this.isMobile) {
        return 'BFP.UI.' + this.plantParametersMobile[index];
      }
      return 'BFP.UI.' + this.plantParameters[index];
    } else {
      if (this.isMobile) {
        return 'BFP.UI.' + this.soilParametersMobile[index];
      }
      return 'BFP.UI.' + this.soilParameters[index];
    }
  }

  getUnit(param: string): string {
    if (this.isPlant()) {
      if (this.plantParameters.includes(param)) {
        return param === 'Foliage_weight' ? 'g' : 'mg/l';
      }
    }
    return 'kg/ha';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
